/**
 * Inline SVG, no icon dependency, and nothing to fetch when offline.
 *
 * Bütün simgeler Tabler Icons, outline (MIT, @tabler/icons 3.49.0,
 * tabler.io/icons), resmi dosyalardan olduğu gibi alındı. Çizgi tabanlı:
 * kalınlık base'deki strokeWidth ile, yer yer CSS'teki stroke-width ile
 * ayarlanıyor. Yeni simge eklerken aynı aileden al ki hepsi uyumlu dursun:
 * https://cdn.jsdelivr.net/npm/@tabler/icons@3.49.0/icons/outline/<ad>.svg
 */

const base = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  // An inline SVG sits on the text baseline and carries the line-height gap
  // with it, which pushes it off-centre inside a button.
  style: { display: 'block' },
}

export function EyeIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
      <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6" />
    </svg>
  )
}

/** Whoever is signing in. */
export function UserIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" />
      <path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" />
    </svg>
  )
}

/** Marks the PIN field. */
export function LockIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M5 13a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v6a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2v-6" />
      <path d="M11 16a1 1 0 1 0 2 0a1 1 0 0 0 -2 0" />
      <path d="M8 11v-4a4 4 0 1 1 8 0v4" />
    </svg>
  )
}

/**
 * Parçalı Öde: tek yol ikiye ayrılıyor, "arrows-split". Fiş biçiminde değil,
 * çünkü fiş zaten ReceiptTextIcon; aynı satırda iki fiş simgesi ayırt edilmiyor.
 */
export function SplitBillIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M21 17h-8l-3.5 -5h-6.5" />
      <path d="M21 7h-8l-3.495 5" />
      <path d="M18 10l3 -3l-3 -3" />
      <path d="M18 20l3 -3l-3 -3" />
    </svg>
  )
}

/** A file going up into the application: importing a menu from a CSV. */
export function UploadIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2" />
      <path d="M7 9l5 -5l5 5" />
      <path d="M12 4l0 12" />
    </svg>
  )
}

/** Whether a slip has been to the stalls. */
export function ReceiptTextIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M5 21v-16a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v16l-3 -2l-2 2l-2 -2l-2 2l-2 -2l-3 2m4 -14h6m-6 4h6m-2 4h2" />
    </svg>
  )
}

/** A slip that will not be printed: the receipt, struck through. */
export function ReceiptOffIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M5 21v-16m2 -2h10a2 2 0 0 1 2 2v10m0 4.01v1.99l-3 -2l-2 2l-2 -2l-2 2l-2 -2l-3 2" />
      <path d="M11 7l4 0" />
      <path d="M9 11l2 0" />
      <path d="M13 15l2 0" />
      <path d="M15 11l0 .01" />
      <path d="M3 3l18 18" />
    </svg>
  )
}

/** Whether the money has been taken. */
export function BanknoteCheckIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M7 15h-3a1 1 0 0 1 -1 -1v-8a1 1 0 0 1 1 -1h12a1 1 0 0 1 1 1v3" />
      <path d="M7 10a1 1 0 0 1 1 -1h12a1 1 0 0 1 1 1v8a1 1 0 0 1 -1 1h-12a1 1 0 0 1 -1 -1l0 -8" />
      <path d="M12 14a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
    </svg>
  )
}

/** Cash taken: notes, nothing else. Pairs with CreditCardIcon at the till. */
export function BanknoteIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" />
      <path d="M3 8a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2l0 -8" />
      <path d="M18 12h.01" />
      <path d="M6 12h.01" />
    </svg>
  )
}

/** İleri: Parçalı Öde'de telefonda sonraki kart (Tabler chevron-right). */
export function ChevronRightIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M9 6l6 6l-6 6" />
    </svg>
  )
}

/** Back to the list a section was opened from. */
export function ChevronLeftIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M15 6l-6 6l6 6" />
    </svg>
  )
}

/** Narrowing the side panel: a pane with one column marked off. */
export function PanelLeftIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M4 6a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2l0 -12" />
      <path d="M9 4l0 16" />
    </svg>
  )
}

/**
 * The same pane with its divider on the right.
 *
 * A separate icon rather than the left one mirrored: mirroring flips the
 * whole frame, and at this size the rounded corners moving is the change the
 * eye catches, not the line. Only the line should move, because the line is
 * what says which side the panel sits on.
 */
export function PanelRightIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M4 6a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2l0 -12" />
      <path d="M15 4l0 16" />
    </svg>
  )
}

/** Money left behind: a coin over an open hand. */
export function HandCoinsIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M19.5 12.572l-7.5 7.428l-7.5 -7.428a5 5 0 1 1 7.5 -6.566a5 5 0 1 1 7.5 6.572" />
      <path d="M12 6l-3.293 3.293a1 1 0 0 0 0 1.414l.543 .543c.69 .69 1.81 .69 2.5 0l1 -1a3.182 3.182 0 0 1 4.5 0l2.25 2.25" />
      <path d="M12.5 15.5l2 2" />
      <path d="M15 13l2 2" />
    </svg>
  )
}

/** Card taken: the stripe is what makes it read as a card and not a note. */
export function CreditCardIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M3 8a3 3 0 0 1 3 -3h12a3 3 0 0 1 3 3v8a3 3 0 0 1 -3 3h-12a3 3 0 0 1 -3 -3l0 -8" />
      <path d="M3 10l18 0" />
      <path d="M7 15l.01 0" />
      <path d="M11 15l2 0" />
    </svg>
  )
}

/** Stok bitti: üstü çizili koli, "package-off". */
export function LayersMinusIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M8.812 4.793l3.188 -1.793l8 4.5v8.5m-2.282 1.784l-5.718 3.216l-8 -4.5v-9l2.223 -1.25" />
      <path d="M14.543 10.57l5.457 -3.07" />
      <path d="M12 12v9" />
      <path d="M12 12l-8 -4.5" />
      <path d="M16 5.25l-4.35 2.447m-2.564 1.442l-1.086 .611" />
      <path d="M3 3l18 18" />
    </svg>
  )
}

/** A reduced price. */
export function PercentIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M9 15l6 -6" />
      <path d="M9 9.5a.5 .5 0 1 0 1 0a.5 .5 0 1 0 -1 0" fill="currentColor" />
      <path d="M14 14.5a.5 .5 0 1 0 1 0a.5 .5 0 1 0 -1 0" fill="currentColor" />
      <path d="M3 12a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
    </svg>
  )
}

/** Finding something rather than adding it. */
export function SearchIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" />
      <path d="M21 21l-6 -6" />
    </svg>
  )
}

/** Confirms a change rather than adding one. */
export function CheckIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M5 12l5 5l10 -10" />
    </svg>
  )
}

export function PlusIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M12 5l0 14" />
      <path d="M5 12l14 0" />
    </svg>
  )
}

export function NoteIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M13 20l7 -7" />
      <path d="M13 20v-6a1 1 0 0 1 1 -1h6v-7a2 2 0 0 0 -2 -2h-12a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h7" />
    </svg>
  )
}

export function PencilIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4" />
      <path d="M13.5 6.5l4 4" />
    </svg>
  )
}

export function PrinterIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M17 17h2a2 2 0 0 0 2 -2v-4a2 2 0 0 0 -2 -2h-14a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2" />
      <path d="M17 9v-4a2 2 0 0 0 -2 -2h-6a2 2 0 0 0 -2 2v4" />
      <path d="M7 15a2 2 0 0 1 2 -2h6a2 2 0 0 1 2 2v4a2 2 0 0 1 -2 2h-6a2 2 0 0 1 -2 -2l0 -4" />
    </svg>
  )
}

/* Drawer navigation. */

/** Gel Al - Kasa (sol menü): alışveriş sepeti, "shopping-cart". */
export function BanknoteArrowUpIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M4 19a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
      <path d="M15 19a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
      <path d="M17 17h-11v-14h-2" />
      <path d="M6 5l14 1l-1 7h-13" />
    </svg>
  )
}

/** logs: the menu, as a list of lines. */
export function LogsIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M9 6l11 0" />
      <path d="M9 12l11 0" />
      <path d="M9 18l11 0" />
      <path d="M5 6l0 .01" />
      <path d="M5 12l0 .01" />
      <path d="M5 18l0 .01" />
    </svg>
  )
}

/** summary: lines gathered into an arrow. */
export function SummaryIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2" />
      <path d="M9 5a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2" />
      <path d="M9 12h6" />
      <path d="M9 16h6" />
    </svg>
  )
}

/** settings: the gear. */
export function SettingsIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065" />
      <path d="M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" />
    </svg>
  )
}

/** Moves a category up or down the menu. */
export function ArrowUpIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M12 5l0 14" />
      <path d="M18 11l-6 -6" />
      <path d="M6 11l6 -6" />
    </svg>
  )
}

export function ArrowDownIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M12 5l0 14" />
      <path d="M18 13l-6 6" />
      <path d="M6 13l6 6" />
    </svg>
  )
}

/** x: closes the drawer. */
export function XIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M18 6l-12 12" />
      <path d="M6 6l12 12" />
    </svg>
  )
}

/** sun-moon: the light/dark switch. */
/** wallet: the accounts kept for people who pay later. */
export function WalletIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M17 8v-3a1 1 0 0 0 -1 -1h-10a2 2 0 0 0 0 4h12a1 1 0 0 1 1 1v3m0 4v3a1 1 0 0 1 -1 1h-12a2 2 0 0 1 -2 -2v-12" />
      <path d="M20 12v4h-4a2 2 0 0 1 0 -4h4" />
    </svg>
  )
}

export function SunMoonIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M9.173 14.83a4 4 0 1 1 5.657 -5.657" />
      <path d="M11.294 12.707l.174 .247a7.5 7.5 0 0 0 8.845 2.492a9 9 0 0 1 -14.671 2.914" />
      <path d="M3 12h1" />
      <path d="M12 3v1" />
      <path d="M5.6 5.6l.7 .7" />
      <path d="M3 21l18 -18" />
    </svg>
  )
}

/** log-out: door with an arrow leaving it. */
export function LogOutIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M14 8v-2a2 2 0 0 0 -2 -2h-7a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h7a2 2 0 0 0 2 -2v-2" />
      <path d="M9 12h12l-3 -3" />
      <path d="M18 15l3 -3" />
    </svg>
  )
}

/** chart-line: the product analysis. */
export function ChartLineIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M4 19l16 0" />
      <path d="M4 15l4 -6l4 2l4 -5l4 4" />
    </svg>
  )
}

export function ChevronUpIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M6 15l6 -6l6 6" />
    </svg>
  )
}

export function ChevronDownIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M6 9l6 6l6 -6" />
    </svg>
  )
}

/**
 * Send-to-back: one square moving behind another. Used for moving a ticket
 * from one table onto another.
 */
export function SendToBackIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M7 10h14l-4 -4" />
      <path d="M17 14h-14l4 4" />
    </svg>
  )
}

export function TrashIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M4 7l16 0" />
      <path d="M10 11l0 6" />
      <path d="M14 11l0 6" />
      <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
      <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
    </svg>
  )
}

/** Üç nokta: kartın ve sekmenin "diğer işlemler" menüsü (Tabler dots). */
export function DotsIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M4 12a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
      <path d="M11 12a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
      <path d="M18 12a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
    </svg>
  )
}

export function EyeOffIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828" />
      <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87" />
      <path d="M3 3l18 18" />
    </svg>
  )
}

/** A letter with a tick: the mail that confirms the address has gone out. */
export function MailCheckIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M11 19h-6a2 2 0 0 1 -2 -2v-10a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v6" />
      <path d="M3 7l9 6l9 -6" />
      <path d="M15 19l2 2l4 -4" />
    </svg>
  )
}

/** Doing the same thing again: sending the mail once more. */
export function RotateCwIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M4.05 11a8 8 0 1 1 .5 4m-.5 5v-5h5" />
    </svg>
  )
}

export function ArrowLeftIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M5 12l14 0" />
      <path d="M5 12l6 6" />
      <path d="M5 12l6 -6" />
    </svg>
  )
}

/** The activation code: a key that opens the institution. */
export function KeyRoundIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M16.555 3.843l3.602 3.602a2.877 2.877 0 0 1 0 4.069l-2.643 2.643a2.877 2.877 0 0 1 -4.069 0l-.301 -.301l-6.558 6.558a2 2 0 0 1 -1.239 .578l-.175 .008h-1.172a1 1 0 0 1 -.993 -.883l-.007 -.117v-1.172a2 2 0 0 1 .467 -1.284l.119 -.13l.414 -.414h2v-2h2v-2l2.144 -2.144l-.301 -.301a2.877 2.877 0 0 1 0 -4.069l2.643 -2.643a2.877 2.877 0 0 1 4.069 0" />
      <path d="M15 9h.01" />
    </svg>
  )
}

/** The business itself, on the setup screen. */
export function StoreIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M3 21l18 0" />
      <path d="M3 7v1a3 3 0 0 0 6 0v-1m0 1a3 3 0 0 0 6 0v-1m0 1a3 3 0 0 0 6 0v-1h-18l2 -4h14l2 4" />
      <path d="M5 21l0 -10.15" />
      <path d="M19 21l0 -10.15" />
      <path d="M9 21v-4a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v4" />
    </svg>
  )
}

export function ArrowRightIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M5 12l14 0" />
      <path d="M13 18l6 -6" />
      <path d="M13 6l6 6" />
    </svg>
  )
}


export function PhoneIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M5 4h4l2 5l-2.5 1.5a11 11 0 0 0 5 5l1.5 -2.5l5 2v4a2 2 0 0 1 -2 2a16 16 0 0 1 -15 -15a2 2 0 0 1 2 -2" />
    </svg>
  )
}

export function MailIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10" />
      <path d="M3 7l9 6l9 -6" />
    </svg>
  )
}

/** Sipariş Takip: a list with ticks, the orders still to take out. */
export function ClipboardListIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2" />
      <path d="M9 5a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2" />
      <path d="M9 12l.01 0" />
      <path d="M13 12l2 0" />
      <path d="M9 16l.01 0" />
      <path d="M13 16l2 0" />
    </svg>
  )
}

/** Sipariş Al: a notebook and pen, the waiter writing an order down. */
export function NotebookPenIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M20 17v-12c0 -1.121 -.879 -2 -2 -2s-2 .879 -2 2v12l2 2l2 -2" />
      <path d="M16 7h4" />
      <path d="M18 19h-13a2 2 0 1 1 0 -4h4a2 2 0 1 0 0 -4h-3" />
    </svg>
  )
}

/** Two arrows chasing each other: this control goes back and forth. */
export function RepeatIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M4 12v-3a3 3 0 0 1 3 -3h13m-3 -3l3 3l-3 3" />
      <path d="M20 12v3a3 3 0 0 1 -3 3h-13m3 3l-3 -3l3 -3" />
    </svg>
  )
}

/** Favorite, on the QR menu. `filled` paints it in the current color. */
export function HeartIcon({ filled = false }: { filled?: boolean }) {
  return filled ? (
    <svg {...base} fill="currentColor" stroke="none" aria-hidden="true">
      <path d="M6.979 3.074a6 6 0 0 1 4.988 1.425l.037 .033l.034 -.03a6 6 0 0 1 4.733 -1.44l.246 .036a6 6 0 0 1 3.364 10.008l-.18 .185l-.048 .041l-7.45 7.379a1 1 0 0 1 -1.313 .082l-.094 -.082l-7.493 -7.422a6 6 0 0 1 3.176 -10.215z" />
    </svg>
  ) : (
    <svg {...base} aria-hidden="true">
      <path d="M19.5 12.572l-7.5 7.428l-7.5 -7.428a5 5 0 1 1 7.5 -6.566a5 5 0 1 1 7.5 6.572" />
    </svg>
  )
}

/**
 * QR menünün dil seçicisi: klasik çeviri işareti, sol üstte Korece "가",
 * sağ altta "A". Yazı tipiyle değil çizgiyle: her telefonda aynı görünsün.
 */
export function LanguagesIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M9 6.371c0 4.418 -2.239 6.629 -5 6.629" />
      <path d="M4 6.371h7" />
      <path d="M5 9c0 2.144 2.252 3.908 6 4" />
      <path d="M12 20l4 -9l4 9" />
      <path d="M19.1 18h-6.2" />
      <path d="M6.694 3l.793 .582" />
    </svg>
  )
}

/** download: örnek dosyayı indir. */
export function DownloadIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2" />
      <path d="M7 11l5 5l5 -5" />
      <path d="M12 4l0 12" />
    </svg>
  )
}

/** Fotoğraf (Görselli menü). */
export function ImageIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M15 8h.01" />
      <path d="M3 6a3 3 0 0 1 3 -3h12a3 3 0 0 1 3 3v12a3 3 0 0 1 -3 3h-12a3 3 0 0 1 -3 -3v-12" />
      <path d="M3 16l5 -5c.928 -.893 2.072 -.893 3 0l5 5" />
      <path d="M14 14l1 -1c.928 -.893 2.072 -.893 3 0l3 3" />
    </svg>
  )
}

/**
 * Masalar (sol menü): dört ayaklı masa. Önde iki uzun ayak, arkada içeride iki
 * kısa ayak: dördü birden görünsün diye hafif derinlik.
 */
/**
 * Masalar (sol menü): dört ayaklı yemek masası. Tabler'da yok, Google Material
 * Symbols "table_restaurant", outlined, ağırlık 500 (Apache-2.0,
 * @material-symbols/svg-500 0.48.0); 500, Tabler'ın çizgisiyle aynı kalınlıkta.
 */
export function TableIcon() {
  return (
    <svg {...base} viewBox="0 -960 960 960" fill="currentColor" stroke="none" aria-hidden="true">
      <path d="M150.78-581.67h657.68l-43.57-154.26H196.35l-45.57 154.26Zm329.46-77.13Zm206.5 145.5H274.26l-14.24 98.32h439.96l-13.24-98.32ZM155.46-155.93l50.43-357.37h-86.61q-21.91 0-34.73-17.68-12.81-17.67-7.09-38.59l57.71-202.63q4-14.19 15.2-23.03 11.2-8.84 26.63-8.84h606q15.2 0 26.39 8.84 11.2 8.84 15.68 23.03l57.71 202.63q5.72 20.92-7.1 38.59-12.81 17.68-34.96 17.68h-85.85l49.91 357.37h-68.37l-26.52-190.92H250.35l-26.76 190.92h-68.13Z" />
    </svg>
  )
}

/** Rapor (sol menü): yükselen ok, "trending-up". */
export function TrendingUpIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M3 17l6 -6l4 4l8 -8" />
      <path d="M14 7l7 0l0 7" />
    </svg>
  )
}

/** Menü (sol menü): çatal ve bıçak, "tools-kitchen-2". */
export function UtensilsIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M19 3v12h-5c-.023 -3.681 .184 -7.406 5 -12m0 12v6h-1v-3m-10 -14v17m-3 -17v3a3 3 0 1 0 6 0v-3" />
    </svg>
  )
}

/** Kiosk tableti, "device-tablet". */
export function TabletIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M5 4a1 1 0 0 1 1 -1h12a1 1 0 0 1 1 1v16a1 1 0 0 1 -1 1h-12a1 1 0 0 1 -1 -1v-16z" />
      <path d="M11 17a1 1 0 1 0 2 0a1 1 0 0 0 -2 0" />
    </svg>
  )
}

/** Mutfak ekranı, "chef-hat". */
export function ChefHatIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M12 3c1.918 0 3.52 1.35 3.91 3.151a4 4 0 0 1 2.09 7.723l0 7.126h-12v-7.126a4 4 0 1 1 2.092 -7.723a4 4 0 0 1 3.908 -3.151" />
      <path d="M6.161 17.009l11.839 -.009" />
    </svg>
  )
}

/** Ses açık, "bell". */
export function BellIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M10 5a2 2 0 1 1 4 0a7 7 0 0 1 4 6v3a4 4 0 0 0 2 3h-16a4 4 0 0 0 2 -3v-3a7 7 0 0 1 4 -6" />
      <path d="M9 17v1a3 3 0 0 0 6 0v-1" />
    </svg>
  )
}

/** Ses kapalı, "bell-off". */
export function BellOffIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M9.346 5.353c.21 -.129 .428 -.246 .654 -.353a2 2 0 1 1 4 0a7 7 0 0 1 4 6v3m-1 3h-13a4 4 0 0 0 2 -3v-3a6.996 6.996 0 0 1 1.273 -3.707" />
      <path d="M9 17v1a3 3 0 0 0 6 0v-1" />
      <path d="M3 3l18 18" />
    </svg>
  )
}

/** Geri al, "arrow-back-up". */
export function UndoIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M9 14l-4 -4l4 -4" />
      <path d="M5 10h11a4 4 0 1 1 0 8h-1" />
    </svg>
  )
}

/** qr-code: QR menü özelliği. */
export function QrCodeIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M4 5a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -4" />
      <path d="M7 17l0 .01" />
      <path d="M14 5a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -4" />
      <path d="M7 7l0 .01" />
      <path d="M4 15a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -4" />
      <path d="M17 7l0 .01" />
      <path d="M14 14l3 0" />
      <path d="M20 14l0 .01" />
      <path d="M14 14l0 3" />
      <path d="M14 20l3 0" />
      <path d="M17 17l3 0" />
      <path d="M20 17l0 3" />
    </svg>
  )
}

/** scale: tartılı ürünler. */
export function ScaleIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M7 20l10 0" />
      <path d="M6 6l6 -1l6 1" />
      <path d="M12 3l0 17" />
      <path d="M9 12l-3 -6l-3 6a3 3 0 0 0 6 0" />
      <path d="M21 12l-3 -6l-3 6a3 3 0 0 0 6 0" />
    </svg>
  )
}

/** info: daire içinde "i", açıklamayı açan düğme. */
export function InfoIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" />
      <path d="M12 9h.01" />
      <path d="M11 12h1v4h1" />
    </svg>
  )
}

/** copy: iki üst üste sayfa (adresi kopyala). */
export function CopyIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M7 9.667a2.667 2.667 0 0 1 2.667 -2.667h8.666a2.667 2.667 0 0 1 2.667 2.667v8.666a2.667 2.667 0 0 1 -2.667 2.667h-8.666a2.667 2.667 0 0 1 -2.667 -2.667l0 -8.666" />
      <path d="M4.012 16.737a2.005 2.005 0 0 1 -1.012 -1.737v-10c0 -1.1 .9 -2 2 -2h10c.75 0 1.158 .385 1.5 1" />
    </svg>
  )
}

/** external-link: yeni sekmede aç. */
export function ExternalLinkIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M12 6h-6a2 2 0 0 0 -2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-6" />
      <path d="M11 13l9 -9" />
      <path d="M15 4h5v5" />
    </svg>
  )
}

/** Google değerlendirme, "star". `filled` içini boyuyor. */
export function StarIcon({ filled = false }: { filled?: boolean }) {
  return (
    <svg {...base} fill={filled ? 'currentColor' : 'none'} aria-hidden="true">
      <path d="M12 17.75l-6.172 3.245l1.179 -6.873l-5 -4.867l6.9 -1l3.086 -6.253l3.086 6.253l6.9 1l-5 4.867l1.179 6.873l-6.173 -3.245" />
    </svg>
  )
}

/** Kampanya, "gift". */
export function GiftIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M3 9a1 1 0 0 1 1 -1h16a1 1 0 0 1 1 1v2a1 1 0 0 1 -1 1h-16a1 1 0 0 1 -1 -1l0 -2" />
      <path d="M12 8l0 13" />
      <path d="M19 12v7a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2v-7" />
      <path d="M7.5 8a2.5 2.5 0 0 1 0 -5a4.8 8 0 0 1 4.5 5a4.8 8 0 0 1 4.5 -5a2.5 2.5 0 0 1 0 5" />
    </svg>
  )
}

/** Tarih alanı, "calendar". */
export function CalendarIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M4 7a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12" />
      <path d="M16 3v4" />
      <path d="M8 3v4" />
      <path d="M4 11h16" />
      <path d="M11 15h1" />
      <path d="M12 15v3" />
    </svg>
  )
}

/** Saat alanı, "clock". */
export function ClockIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" />
      <path d="M12 7v5l3 3" />
    </svg>
  )
}

/** notebook: Cari, satışı bir kişinin hesabına yaz. */
export function AddressBookIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M6 4h11a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-11a1 1 0 0 1 -1 -1v-14a1 1 0 0 1 1 -1m3 0v18" />
      <path d="M13 8l2 0" />
      <path d="M13 12l2 0" />
    </svg>
  )
}

/*
 * Ödeme düğmeleri (altısı) Phosphor Icons, Light ağırlık (MIT,
 * @phosphor-icons/core 2.1.1), kullanıcı bunları ayrıca seçti; gerisi Tabler.
 * Dolgu ile çiziliyor, stroke-width bunları etkilemez. Kaynak:
 * https://cdn.jsdelivr.net/npm/@phosphor-icons/core@2.1.1/assets/light/<ad>-light.svg
 */
const phosphor = {
  width: 16,
  height: 16,
  viewBox: '0 0 256 256',
  fill: 'currentColor',
  style: { display: 'block' },
}

/** seal-percent: İndirimli. */
export function PhSealPercentIcon() {
  return (
    <svg {...phosphor} aria-hidden="true">
      <path d="M224.42,104.2c-3.9-4.07-7.93-8.27-9.55-12.18-1.5-3.63-1.58-9-1.67-14.68-.14-9.38-.3-20-7.42-27.12S188,42.94,178.66,42.8c-5.68-.09-11-.17-14.68-1.67-3.91-1.62-8.11-5.65-12.18-9.55C145.16,25.22,137.64,18,128,18s-17.16,7.22-23.8,13.58c-4.07,3.9-8.27,7.93-12.18,9.55-3.63,1.5-9,1.58-14.68,1.67-9.38.14-20,.3-27.12,7.42S42.94,68,42.8,77.34c-.09,5.68-.17,11-1.67,14.68-1.62,3.91-5.65,8.11-9.55,12.18C25.22,110.84,18,118.36,18,128s7.22,17.16,13.58,23.8c3.9,4.07,7.93,8.27,9.55,12.18,1.5,3.63,1.58,9,1.67,14.68.14,9.38.3,20,7.42,27.12S68,213.06,77.34,213.2c5.68.09,11,.17,14.68,1.67,3.91,1.62,8.11,5.65,12.18,9.55C110.84,230.78,118.36,238,128,238s17.16-7.22,23.8-13.58c4.07-3.9,8.27-7.93,12.18-9.55,3.63-1.5,9-1.58,14.68-1.67,9.38-.14,20-.3,27.12-7.42s7.28-17.74,7.42-27.12c.09-5.68.17-11,1.67-14.68,1.62-3.91,5.65-8.11,9.55-12.18C230.78,145.16,238,137.64,238,128S230.78,110.84,224.42,104.2Zm-8.66,39.29c-4.67,4.87-9.5,9.91-12,15.91-2.38,5.74-2.48,12.52-2.58,19.08-.11,7.44-.23,15.14-3.9,18.82s-11.38,3.79-18.82,3.9c-6.56.1-13.34.2-19.08,2.58-6,2.48-11,7.31-15.91,12-5.25,5-10.68,10.24-15.49,10.24s-10.24-5.21-15.5-10.24c-4.86-4.67-9.9-9.5-15.9-12-5.74-2.38-12.52-2.48-19.08-2.58-7.44-.11-15.14-.23-18.82-3.9s-3.79-11.38-3.9-18.82c-.1-6.56-.2-13.34-2.58-19.08-2.48-6-7.31-11-12-15.91C35.21,138.24,30,132.81,30,128s5.21-10.24,10.24-15.49c4.67-4.87,9.5-9.91,12-15.91,2.38-5.74,2.48-12.52,2.58-19.08.11-7.44.23-15.14,3.9-18.82s11.38-3.79,18.82-3.9c6.56-.1,13.34-.2,19.08-2.58,6-2.48,11-7.31,15.91-12C117.76,35.21,123.19,30,128,30s10.24,5.21,15.5,10.24c4.86,4.67,9.9,9.5,15.9,12,5.74,2.38,12.52,2.48,19.08,2.58,7.44.11,15.14.23,18.82,3.9s3.79,11.38,3.9,18.82c.1,6.56.2,13.34,2.58,19.08,2.48,6,7.31,11,12,15.91,5,5.25,10.24,10.68,10.24,15.49S220.79,138.24,215.76,143.49ZM118,96a22,22,0,1,0-22,22A22,22,0,0,0,118,96ZM86,96a10,10,0,1,1,10,10A10,10,0,0,1,86,96Zm74,42a22,22,0,1,0,22,22A22,22,0,0,0,160,138Zm0,32a10,10,0,1,1,10-10A10,10,0,0,1,160,170Zm12.24-77.76-80,80a6,6,0,0,1-8.48-8.48l80-80a6,6,0,0,1,8.48,8.48Z" />
    </svg>
  )
}

/** printer: Öde, Yazdır & Kapat. */
export function PhPrinterIcon() {
  return (
    <svg {...phosphor} aria-hidden="true">
      <path d="M214.67,74H198V40a6,6,0,0,0-6-6H64a6,6,0,0,0-6,6V74H41.33C28.47,74,18,83.87,18,96v80a6,6,0,0,0,6,6H58v34a6,6,0,0,0,6,6H192a6,6,0,0,0,6-6V182h34a6,6,0,0,0,6-6V96C238,83.87,227.53,74,214.67,74ZM70,46H186V74H70ZM186,210H70V158H186Zm40-40H198V152a6,6,0,0,0-6-6H64a6,6,0,0,0-6,6v18H30V96c0-5.51,5.08-10,11.33-10H214.67C220.92,86,226,90.49,226,96Zm-28-54a10,10,0,1,1-10-10A10,10,0,0,1,198,116Z" />
    </svg>
  )
}

/** hand-coins: Üstü Kalsın. */
export function PhHandCoinsIcon() {
  return (
    <svg {...phosphor} aria-hidden="true">
      <path d="M229.12,142.65a22.43,22.43,0,0,0-19.55-3.88l-45.24,10.4A26,26,0,0,0,140,114H89.94a29.78,29.78,0,0,0-21.21,8.79L45.52,146H16A14,14,0,0,0,2,160v40a14,14,0,0,0,14,14H120a6,6,0,0,0,1.46-.18l64-16a7.16,7.16,0,0,0,.89-.3L225.17,181l.33-.15a22.6,22.6,0,0,0,3.62-38.18ZM14,200V160a2,2,0,0,1,2-2H42v44H16A2,2,0,0,1,14,200Zm206.28-30-38.2,16.27L119.26,202H54V154.49l23.21-23.22A17.88,17.88,0,0,1,89.94,126H140a14,14,0,0,1,0,28H112a6,6,0,0,0,0,12h32a6,6,0,0,0,1.34-.15l67-15.41.24-.06A10.6,10.6,0,0,1,220.28,170ZM164,94a34.54,34.54,0,0,0,7.28-.78,34,34,0,1,0,25.46-42.43A34,34,0,1,0,164,94Zm62-10a22,22,0,1,1-22-22A22,22,0,0,1,226,84ZM164,38a21.94,21.94,0,0,1,21.51,17.49,34,34,0,0,0-15.38,25.65A22,22,0,1,1,164,38Z" />
    </svg>
  )
}

/** address-book: Cari. */
export function PhAddressBookIcon() {
  return (
    <svg {...phosphor} aria-hidden="true">
      <path d="M159.11,142.13a38,38,0,1,0-46.22,0A65.75,65.75,0,0,0,83.2,164.4a6,6,0,0,0,9.6,7.2,54,54,0,0,1,86.4,0,6,6,0,0,0,9.6-7.2A65.75,65.75,0,0,0,159.11,142.13ZM110,112a26,26,0,1,1,26,26A26,26,0,0,1,110,112Zm98-86H64A14,14,0,0,0,50,40V66H32a6,6,0,0,0,0,12H50v44H32a6,6,0,0,0,0,12H50v44H32a6,6,0,0,0,0,12H50v26a14,14,0,0,0,14,14H208a14,14,0,0,0,14-14V40A14,14,0,0,0,208,26Zm2,190a2,2,0,0,1-2,2H64a2,2,0,0,1-2-2V40a2,2,0,0,1,2-2H208a2,2,0,0,1,2,2Z" />
    </svg>
  )
}

/** fork-knife: Yemek kartı (21 Eki 2026). */
export function PhForkKnifeIcon() {
  return (
    <svg {...phosphor} aria-hidden="true">
      <path d="M74,88V40a6,6,0,0,1,12,0V88a6,6,0,0,1-12,0ZM214,40V224a6,6,0,0,1-12,0V174H152a6,6,0,0,1-6-6c0-4.41.68-108.25,59.64-133.51A6,6,0,0,1,214,40ZM202,50c-36.79,24.29-42.82,91.48-43.81,112H202ZM117.92,39a6,6,0,1,0-11.84,2L114,88.48a34,34,0,0,1-68,0L53.92,41a6,6,0,0,0-11.84-2l-8,48A6.61,6.61,0,0,0,34,88a46.06,46.06,0,0,0,40,45.6V224a6,6,0,0,0,12,0V133.6A46.06,46.06,0,0,0,126,88a6.61,6.61,0,0,0-.08-1Z" />
    </svg>
  )
}

/** money: Öde & Kapat. */
export function PhMoneyIcon() {
  return (
    <svg {...phosphor} aria-hidden="true">
      <path d="M128,166a38,38,0,1,0-38-38A38,38,0,0,0,128,166Zm0-64a26,26,0,1,1-26,26A26,26,0,0,1,128,102ZM240,58H16a6,6,0,0,0-6,6V192a6,6,0,0,0,6,6H240a6,6,0,0,0,6-6V64A6,6,0,0,0,240,58ZM22,108.82A54.73,54.73,0,0,0,60.82,70H195.18A54.73,54.73,0,0,0,234,108.82v38.36A54.73,54.73,0,0,0,195.18,186H60.82A54.73,54.73,0,0,0,22,147.18ZM234,96.29A42.8,42.8,0,0,1,207.71,70H234ZM48.29,70A42.8,42.8,0,0,1,22,96.29V70ZM22,159.71A42.8,42.8,0,0,1,48.29,186H22ZM207.71,186A42.8,42.8,0,0,1,234,159.71V186Z" />
    </svg>
  )
}

/** arrows-split: Parçalı Öde. */
export function PhArrowsSplitIcon() {
  return (
    <svg {...phosphor} aria-hidden="true">
      <path d="M228.24,188.24l-32,32a6,6,0,0,1-8.48,0l-32-32a6,6,0,0,1,8.48-8.48L186,201.51v-63l-58-58-58,58v63l21.76-21.75a6,6,0,0,1,8.48,8.48l-32,32a6,6,0,0,1-8.48,0l-32-32a6,6,0,0,1,8.48-8.48L58,201.51V136a6,6,0,0,1,1.76-4.24L122,69.51V24a6,6,0,0,1,12,0V69.51l62.24,62.25A6,6,0,0,1,198,136v65.51l21.76-21.75a6,6,0,0,1,8.48,8.48Z" />
    </svg>
  )
}

/** Yazıcı Köprüsü (Ayarlar > Yazıcılar): "plug-connected". */
export function PlugConnectedIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M7 12l5 5l-1.5 1.5a3.536 3.536 0 1 1 -5 -5l1.5 -1.5" />
      <path d="M17 12l-5 -5l1.5 -1.5a3.536 3.536 0 1 1 5 5l-1.5 1.5" />
      <path d="M3 21l2.5 -2.5" />
      <path d="M18.5 5.5l2.5 -2.5" />
      <path d="M10 11l-2 2" />
      <path d="M13 14l-2 2" />
    </svg>
  )
}

/** hand-waving (Phosphor Light): garsonun üst çubuğundaki selam. */
export function PhHandWavingIcon() {
  return (
    <svg {...phosphor} aria-hidden="true">
      <path d="M218.44,101,201.13,71a26,26,0,0,0-45.58,1L136.47,39a26,26,0,0,0-46.14,2.19A26,26,0,0,0,49.88,73l3.29,5.7a26,26,0,0,0-23.69,39L69.5,187a85.44,85.44,0,0,0,52.21,40.08,86.71,86.71,0,0,0,22.4,3,86,86,0,0,0,74.33-129Zm-3,62.16A74,74,0,0,1,79.89,181l-40-69.32a14,14,0,0,1,24.25-14L83.36,131a6,6,0,0,0,10.39-6L60.27,67A14,14,0,1,1,84.52,53l31.17,54a6,6,0,0,0,5.2,3,5.9,5.9,0,0,0,3-.81,6,6,0,0,0,2.2-8.2L101.83,59a14,14,0,0,1,24.25-14l34.2,59.23a46,46,0,0,0-6.4,59.13,6,6,0,0,0,9.94-6.73,34,34,0,0,1,7.76-46.26,6,6,0,0,0,1.6-7.8L166.49,91a14,14,0,1,1,24.25-14l17.31,30A73.53,73.53,0,0,1,215.44,163.14ZM186.18,32.22A6,6,0,0,1,193.52,28a57.59,57.59,0,0,1,35.32,27l.33.57a6,6,0,1,1-10.39,6l-.33-.57a45.69,45.69,0,0,0-28-21.43A6,6,0,0,1,186.18,32.22ZM79.32,235.71a6,6,0,0,1-8.42,1A117.79,117.79,0,0,1,41.79,203a6,6,0,0,1,10.4-6,105.48,105.48,0,0,0,26.14,30.29A6,6,0,0,1,79.32,235.71Z" />
    </svg>
  )
}

/** Sürükleyip taşıma kulbu (Menü Yönetimi): "grip-vertical". */
export function GripVerticalIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M9 5m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
      <path d="M9 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
      <path d="M9 19m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
      <path d="M15 5m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
      <path d="M15 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
      <path d="M15 19m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
    </svg>
  )
}

/** Barkod okut simgesi: köşe çerçevesi içinde çizgiler. */
export function ScanBarcodeIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'block' }}
      aria-hidden="true"
    >
      <path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2" />
      <path d="M8 8v8M11 8v8M14 8v8M17 8v8" />
    </svg>
  )
}


/** QR Sipariş: masa çağırıyor, "bell-ringing". */
export function BellRingIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M10 5a2 2 0 0 1 4 0a7 7 0 0 1 4 6v3a4 4 0 0 0 2 3h-16a4 4 0 0 0 2 -3v-3a7 7 0 0 1 4 -6" />
      <path d="M9 17v1a3 3 0 0 0 6 0v-1" />
      <path d="M21 6.727a11.05 11.05 0 0 0 -2.794 -3.727" />
      <path d="M3 6.727a11.05 11.05 0 0 1 2.792 -3.727" />
    </svg>
  )
}

export function MinusIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M5 12l14 0" />
    </svg>
  )
}

/** QR Sipariş: müşterinin sepeti, "shopping-bag". */
export function ShoppingBagIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M6.331 8h11.339a2 2 0 0 1 1.977 2.304l-1.255 8.152a3 3 0 0 1 -2.966 2.544h-6.852a3 3 0 0 1 -2.965 -2.544l-1.255 -8.152a2 2 0 0 1 1.977 -2.304" />
      <path d="M9 11v-5a3 3 0 0 1 6 0v5" />
    </svg>
  )
}

/** Masa karekodunu yenile, "refresh". */
export function RefreshIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4" />
      <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4" />
    </svg>
  )
}

/** filter: tablo sütun filtresi (1 Kas 2026). */
export function FilterIcon() {
  return (
    <svg {...base} aria-hidden="true">
      <path d="M4 4h16v2.172a2 2 0 0 1 -.586 1.414l-4.414 4.414v7l-6 2v-8.5l-4.48 -4.928a2 2 0 0 1 -.52 -1.345v-2.227z" />
    </svg>
  )
}
